import React, { useState, ChangeEvent } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useSelector } from "react-redux";
import U from "../../../felles/Utils";
import axios from 'axios';
import CSharpUtils from '../../../felles/CSharpUtils';
import {
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Tabs,
  Tab,
  Box,
  Container,
  Tooltip,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DescriptionIcon from "@mui/icons-material/Description";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InfoIcon from "@mui/icons-material/Info";
import "./UplAlt1File.css";

// 🔹 Typer
type FormInputs = {
  file: FileList;
  email: string;
};

type FileRow = {
  firma: string;
  alfa: string;
  artnr: string;
  alt1Alfa: string;
  alt1Artnr: string;
  edbnr: string;
  slett: string;
};

// 🔹 Type for AppState (tilpass til din faktiske state struktur)
interface AppState {
  auth: {
    userJWT: {
      companyNr?: string;
      Department?: string;
      // andre properties...
    };
  };
}

// API function for Alt1 file upload
export interface UploadAlt1FileResult {
  success: boolean;
  errorMessage?: string;
}

export async function uploadAlt1FileWithEmail(formData: FormData): Promise<UploadAlt1FileResult> {
  try {
    const dotNetApiUrl = `${U.URL_ADDRESS}${U.DOTNET_DEV_SERVICE}/v1/test/varetek/UploadAlt1FileWithEmail`;

    const config = {
      method: 'post',
      baseURL: dotNetApiUrl,
      data: formData,
      headers: {
        Authorization: `Bearer ${CSharpUtils.Get_Token}`,
      },
      timeout: 80000,
      withCredentials: false,
      secure: false,
      changeOrigin: true,
      mode: 'no-cors',
    };

    await axios.request(config);
    return { success: true };
  } catch (error: any) {
    U.error("Upload error:", error);
    U.error("Error response:", error.response);
    U.error("Error response data:", error.response?.data);
    U.error("Error status:", error.response?.status);

    let errorMessage = "Ukjent feil";
    if (error.response?.data) {
      if (typeof error.response.data === 'string') {
        errorMessage = error.response.data;
      } else if (error.response.data.message) {
        errorMessage = error.response.data.message;
      } else {
        errorMessage = JSON.stringify(error.response.data);
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return { success: false, errorMessage };
  }
}

const UploadAlt1File: React.FC = () => {
  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm<FormInputs>();
  const [uploadStatus, setUploadStatus] = useState<string>("");
  const [filePreview, setFilePreview] = useState<FileRow[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [inputKey, setInputKey] = useState<number>(0);
  const [submitAttempted, setSubmitAttempted] = useState<boolean>(false);
  
  // 🔹 Hent firma fra Redux state
  const userJWT = useSelector((state: AppState) => state.auth.userJWT);
  const companyNr = userJWT?.companyNr || userJWT?.Department || ''; // Check both property names
  
  // 🔍 Debug logging to see what's in userJWT
  U.log("🔍 DEBUG - userJWT object:", userJWT);
  U.log("🔍 DEBUG - Available userJWT properties:", userJWT ? Object.keys(userJWT) : "userJWT is null/undefined");
  U.log("🔍 DEBUG - companyNr value:", companyNr);
  U.log("🔍 DEBUG - Department value (if exists):", userJWT?.Department);

  // 📄 Leser innholdet i valgt fil
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    
    // Clear previous file preview and status immediately
    setFilePreview([]);
    setUploadStatus("");
    setSelectedFile(file || null);
    setSubmitAttempted(false); // Reset submit attempt when new file is selected
    
    U.log("File selected:", file?.name, "Size:", file?.size);
    
    if (!file) {
      U.log("No file selected, returning");
      return;
    }
    
    if (!companyNr) {
      U.log("No company number available, using empty string");
      setUploadStatus("⚠️ Firmainformasjon ikke tilgjengelig - fil vil ikke inneholde firmadata.");
      // Continue processing but warn user
    }
    
    const reader = new FileReader();
    
    // Add file size validation
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

    if (file.size > MAX_FILE_SIZE) {
      setUploadStatus("❌ Fil er for stor (maks 5MB)");
      return;
    }

    // Add error handling for file parsing
    try {
      reader.readAsText(file, "UTF-8");
    } catch (error) {
      setUploadStatus("❌ Kunne ikke lese fil");
      U.error("File read error:", error);
      return;
    }

    reader.onload = (e: ProgressEvent<FileReader>) => {
      U.log("File read successfully");
      const text = e.target?.result;
      if (typeof text === "string") {
        U.log("File content length:", text.length);
        const lines = text
          .split("\n")
          .map(line => line.trim())
          .filter(line => line);
        U.log("Lines found:", lines.length);
        
        const parsedData: FileRow[] = lines
          .map(line => line.split(";"))
          .filter(parts => parts.length >= 5) // Minimum 5 felter for Alt1: ALFA;ARTNR;ALT1_ALFA;ALT1_ARTNR;EDBNR (SLETT er valgfri)
          .filter(parts => {
            // Filtrer bort header-rad hvis den eksisterer
            const firstColumn = parts[0]?.trim().toUpperCase();
            return firstColumn !== "ALFA" && firstColumn !== "FIRMA";
          })
          .map(parts => ({
            firma: companyNr || '', // 🔹 Bruker companyNr fra Redux state med fallback til tom string
            alfa: parts[0], // ALFA
            artnr: parts[1], // ARTNR  
            alt1Alfa: parts[2], // ALT1 ALFA
            alt1Artnr: parts[3], // ALT1 ARTNR
            edbnr: parts[4] || "", // EDBNR
            slett: parts[5] || "", // SLETT (kan være tom)
          }));
        U.log("Parsed data rows:", parsedData.length);
        setFilePreview(parsedData);
        
        // Reset input by changing key to allow selecting same file again
        setInputKey(prev => prev + 1);
      }
    };
  };

  // 📤 Laster opp fil til API
  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    setSubmitAttempted(true);
    
    // Debug logging
    U.log("Submit data:", data);
    U.log("File object:", data.file);
    U.log("File length:", data.file?.length);
    U.log("First file:", data.file?.[0]);
    U.log("Selected file state:", selectedFile);
    
    // Bruk selectedFile som fallback hvis react-hook-form ikke har filen
    const fileToUpload = data.file?.[0] || selectedFile;
    
    if (!fileToUpload) {
      setUploadStatus("❌ Du må velge en fil.");
      return;
    }

    if (!companyNr) {
      setUploadStatus("⚠️ Firmainformasjon ikke tilgjengelig - filen vil ikke inneholde firmadata.");
      U.warn("Proceeding with upload despite missing companyNr");
      // Continue processing with empty company number
    }

    setUploadStatus("Opplasting pågår...");

    // Lag CSV-innhold med firma som første kolonne (som .NET Controller forventer)
    // Filtrer bort eventuelle header-rader før sending til backend
    const csvContent = filePreview
      .filter(row => {
        // Ekstra sikkerhet: Fjern eventuelle header-rader som kan ha sluppet gjennom
        const alfa = row.alfa?.trim().toUpperCase();
        return alfa !== "ALFA" && alfa !== "FIRMA";
      })
      .map(row => `${row.firma};${row.alfa};${row.artnr};${row.alt1Alfa};${row.alt1Artnr};${row.edbnr};${row.slett}`)
      .join('\n');
    
    const modifiedFile = new File([csvContent], fileToUpload.name, { type: 'text/csv' });

    const formData = new FormData();
    formData.append("file", modifiedFile);
    formData.append("email", data.email);
    
    // Debug logging
    U.log("=== DEBUG INFO ===");
    U.log("File name:", modifiedFile.name);
    U.log("File size:", modifiedFile.size);
    U.log("File type:", modifiedFile.type);
    U.log("Email:", data.email);
    U.log("CSV Content preview:", csvContent.substring(0, 200) + "...");
    U.log("File preview rows:", filePreview.length);
    U.log("Company Nr:", companyNr);

    const result = await uploadAlt1FileWithEmail(formData);
    if (result.success) {
      setUploadStatus(`✅ Opplasting vellykket, kvitteringsliste sendt til ${data.email}.`);
      setFilePreview([]);
      setSelectedFile(null);
      setSubmitAttempted(false); // Reset submit attempt after successful upload
      reset();
    } else {
      setUploadStatus(`❌ Feil ved opplasting: ${result.errorMessage}`);
    }
  };

  // 🚨 Warning if companyNr is missing but don't block rendering
  const showCompanyWarning = !companyNr;
  if (showCompanyWarning) {
    U.warn("⚠️ CompanyNr not available from userJWT. Component will render but API calls may fail.");
  }

  return (
    <Container maxWidth={false} sx={{ mt: 1, pr: 2, pl: 1, ml: 0 }}>
      <Box sx={{ maxWidth: '1200px' }}>
        <Paper elevation={0} sx={{ bgcolor: 'transparent' }}>
          
          {/* Company Warning */}
          {showCompanyWarning && (
            <Paper elevation={1} sx={{ p: 1.5, mb: 2, bgcolor: "#fff3cd", borderColor: "#ffeaa7" }}>
              <Typography variant="body2" sx={{ color: "#856404" }}>
                ⚠️ Firmainformasjon ikke tilgjengelig. Noen funksjoner kan være begrenset.
              </Typography>
            </Paper>
          )}
          
          {/* Header */}
          <Typography variant="h4" component="h1" sx={{ mb: 1, fontWeight: 400 }}>
            Opplasting fil ALT1 
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Last opp alternativt oppslag på artikkel i ALT1.
          </Typography>

          {/* Nedlastingsknapper */}
          <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
          <Button
            variant="outlined"
            href={`${U.URL_ADDRESS}${U.HOMEPAGE_CONTEXT}/files/Mal_for_alt1_endr.csv`}
            download
            startIcon={<DescriptionIcon />}
            sx={{ 
              textTransform: 'none',
              borderColor: '#e0e0e0',
              color: '#666666',
              '&:hover': { 
                borderColor: '#d32f2f',
                backgroundColor: '#d32f2f',
                color: 'white'
              }
            }}
          >
            Last ned mal
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              variant="outlined"
              href={`${U.URL_ADDRESS}${U.HOMEPAGE_CONTEXT}/files/Beskrivelse_alt1_endring.xlsx`}
              download
              startIcon={<InfoOutlinedIcon />}
              sx={{ 
                textTransform: 'none',
                borderColor: '#e0e0e0',
                color: '#666666',
                '&:hover': { 
                  borderColor: '#d32f2f',
                  backgroundColor: '#d32f2f',
                  color: 'white'
                }
              }}
            >
              Feltforklaring
            </Button>
            
            <Tooltip
              title={
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {"Programmet benyttes til å laste opp endringer til Alt1 feltet i VAVE. Last ned malen, fyll den ut, og last deretter filen opp i systemet. Husk å legge inn din e-post for kvitteringsliste.\n\nI malen skal du angi alfa-/artikkelnummer eller EDB-nummer for artiklene, samt nye Alt1 alfa- og artikkelnumre som skal erstatte de eksisterende. Sett 'Slett'-feltet til 1 for å slette en eksisterende Alt1-kobling.\n\nFor å opprette nye Alt1-koblinger fyller du ut både eksisterende og nye verdier. For å slette eksisterende koblinger setter du 'Slett' til 1 og lar nye verdier stå tomme."}
                </Typography>
              }
              arrow
            >
              <InfoIcon style={{ cursor: "pointer", marginLeft: "10px", color: "#1d6f42" }} />
            </Tooltip>
          </Box>
        </Stack>

        {/* Upload Section */}
        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 1.5, display: 'flex', alignItems: 'center' }}>
            <CloudUploadIcon sx={{ mr: 1 }} />
            Last opp fil
          </Typography>

          <Stack spacing={1.5}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>Velg fil</Typography>
              <Button
                variant="contained"
                component="label"
                sx={{
                  bgcolor: '#d32f2f',
                  '&:hover': { bgcolor: '#b71c1c' },
                  textTransform: 'none'
                }}
              >
                Velg Fil
                <input
                  key={inputKey}
                  type="file"
                  accept=".csv,.txt"
                  hidden
                  {...register("file")}
                  onChange={handleFileChange}
                />
              </Button>
              {submitAttempted && !selectedFile && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                  Fil er påkrevd
                </Typography>
              )}
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>E-post for kvitteringsliste</Typography>
              <TextField
                type="email"
                placeholder="din@epost.no"
                {...register("email", { required: "E-post må fylles ut" })}
                error={!!errors.email}
                helperText={errors.email?.message}
                fullWidth
                size="small"
              />
            </Box>

            <Box>
              <Button 
                variant="contained" 
                color="success" 
                type="button"
                onClick={handleSubmit(onSubmit)}
                sx={{ textTransform: 'none' }}
              >
                Send inn fil for oppdatering
              </Button>
            </Box>

            {/* Display selected file name */}
            {selectedFile && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Valgt fil:</strong> {selectedFile.name}
                </Typography>
              </Box>
            )}
          </Stack>
        </Paper>

        {/* Status Message */}
        {uploadStatus && (
          <Paper elevation={1} sx={{ p: 1.5, mb: 2, bgcolor: uploadStatus.startsWith("✅") ? "#e8f5e8" : "#ffebee" }}>
            <Typography
              variant="body2"
              sx={{ color: uploadStatus.startsWith("✅") ? "green" : "red" }}
            >
              {uploadStatus}
            </Typography>
          </Paper>
        )}

        {/* Preview Table */}
        {filePreview.length > 0 && (
          <Paper elevation={1} sx={{ overflow: 'hidden' }}>
            <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6">
                Forhåndsvisning av fil ({filePreview.length} linjer)
              </Typography>
            </Box>
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Firma</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Alfa</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Artnr</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Alt1 Alfa</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Alt1 Artnr</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Edbnr</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Slett</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filePreview.map((row, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{row.firma}</TableCell>
                      <TableCell>{row.alfa}</TableCell>
                      <TableCell>{row.artnr}</TableCell>
                      <TableCell>{row.alt1Alfa}</TableCell>
                      <TableCell>{row.alt1Artnr}</TableCell>
                      <TableCell>{row.edbnr}</TableCell>
                      <TableCell>{row.slett}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Paper>
        )}
        </Paper>
      </Box>
    </Container>
  );
};

export default UploadAlt1File;